import { useState } from 'react';
import {
  ActionIcon,
  Badge,
  Button,
  Group,
  Paper,
  Select,
  Stack,
  Text,
  TextInput,
  Textarea,
  Tooltip,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import {
  IconChevronDown,
  IconChevronUp,
  IconDeviceFloppy,
  IconPlus,
  IconTrash,
} from '@tabler/icons-react';
import {
  TRAINING_BLOCK_LABELS,
  TRAINING_BLOCK_TYPES,
  type TrainingBlockType,
  type TrainingBlockView,
} from '@creativitas/shared';
import { ImageField } from './ImageField';

/**
 * Simple block builder (§45): add, edit, reorder, delete.
 *
 * Deliberately not a rich Notion-like editor — a type, an optional title and
 * either text or a link. Reordering uses buttons rather than drag-and-drop:
 * a short vertical list does not need a drag gesture (§63).
 */

const NEEDS_URL: TrainingBlockType[] = ['video', 'audio', 'external_link', 'pdf'];

const URL_PLACEHOLDER: Record<TrainingBlockType, string> = {
  text: '',
  image: '',
  video: 'https://www.youtube.com/watch?v=...',
  audio: 'https://...',
  external_link: 'https://... или /assets/...',
  pdf: 'https://.../file.pdf',
};

type BlockEditorProps = {
  block: TrainingBlockView;
  index: number;
  total: number;
  saving: boolean;
  onSave: (blockId: string, patch: { title: string | null; body: string | null; url: string | null }) => void;
  onDelete: (blockId: string) => void;
  onMove: (blockId: string, direction: -1 | 1) => void;
};

function BlockEditor({ block, index, total, saving, onSave, onDelete, onMove }: BlockEditorProps) {
  const [title, setTitle] = useState(block.title ?? '');
  const [body, setBody] = useState(block.body ?? '');
  const [url, setUrl] = useState(block.url ?? '');

  const dirty =
    title !== (block.title ?? '') || body !== (block.body ?? '') || url !== (block.url ?? '');

  return (
    <Paper withBorder p="md" radius="lg" style={{ borderColor: 'var(--border-subtle)' }}>
      <Stack gap="sm">
        <Group justify="space-between" wrap="nowrap">
          <Group gap="xs">
            <Badge variant="light">{index + 1}</Badge>
            <Badge variant="default">{TRAINING_BLOCK_LABELS[block.type]}</Badge>
            {block.type === 'video' && block.url && !block.embedUrl ? (
              <Badge size="xs" variant="light" color="gold">
                встраивание недоступно — будет ссылка
              </Badge>
            ) : null}
          </Group>

          <Group gap={4} wrap="nowrap">
            <Tooltip label="Выше">
              <ActionIcon
                variant="subtle"
                aria-label={`Переместить блок ${index + 1} выше`}
                disabled={index === 0}
                onClick={() => onMove(block.id, -1)}
              >
                <IconChevronUp size={16} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Ниже">
              <ActionIcon
                variant="subtle"
                aria-label={`Переместить блок ${index + 1} ниже`}
                disabled={index === total - 1}
                onClick={() => onMove(block.id, 1)}
              >
                <IconChevronDown size={16} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Удалить блок">
              <ActionIcon
                variant="subtle"
                color="red"
                aria-label={`Удалить блок ${index + 1}`}
                onClick={() =>
                  modals.openConfirmModal({
                    title: 'Удалить блок?',
                    labels: { confirm: 'Удалить', cancel: 'Отмена' },
                    confirmProps: { color: 'red' },
                    onConfirm: () => onDelete(block.id),
                  })
                }
              >
                <IconTrash size={16} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Group>

        <TextInput
          label="Заголовок"
          placeholder="Необязательно"
          value={title}
          onChange={(event) => setTitle(event.currentTarget.value)}
        />

        {block.type === 'text' ? (
          <Textarea
            label="Текст"
            minRows={4}
            autosize
            maxRows={16}
            value={body}
            onChange={(event) => setBody(event.currentTarget.value)}
          />
        ) : null}

        {NEEDS_URL.includes(block.type) ? (
          <TextInput
            label="Ссылка"
            placeholder={URL_PLACEHOLDER[block.type]}
            value={url}
            onChange={(event) => setUrl(event.currentTarget.value)}
          />
        ) : null}

        {block.type === 'text' || block.type === 'image' ? (
          <ImageField
            testId={`block-image-${index}`}
            label={block.type === 'image' ? 'Картинка' : 'Иллюстрация'}
            description={
              block.type === 'image'
                ? 'WebP, JPEG, PNG, AVIF или GIF — движущаяся картинка останется движущейся.'
                : 'Необязательно. WebP, JPEG, PNG, AVIF или GIF — движущаяся картинка останется движущейся.'
            }
            value={url || null}
            disabled={saving}
            /*
              Картинка сохраняется сразу вместе с блоком, а не ждёт кнопки:
              загруженный файл уже лежит на сервере, и оставить его
              неприкреплённым — верный способ потерять работу на перезагрузке.
            */
            onChange={(next) => {
              setUrl(next ?? '');
              onSave(block.id, {
                title: title.trim() || null,
                body: body.trim() || null,
                url: next,
              });
            }}
          />
        ) : null}

        {block.type === 'image' ? (
          <Textarea
            label="Подпись"
            description="Необязательно: строка под картинкой."
            minRows={2}
            autosize
            value={body}
            onChange={(event) => setBody(event.currentTarget.value)}
          />
        ) : null}

        {block.type === 'external_link' ? (
          <Textarea
            label="Пояснение"
            minRows={2}
            value={body}
            onChange={(event) => setBody(event.currentTarget.value)}
          />
        ) : null}

        {dirty ? (
          <Group>
            <Button
              size="xs"
              leftSection={<IconDeviceFloppy size={14} />}
              loading={saving}
              onClick={() =>
                onSave(block.id, {
                  title: title.trim() || null,
                  body: body.trim() || null,
                  url: url.trim() || null,
                })
              }
            >
              Сохранить блок
            </Button>
            <Button
              size="xs"
              variant="subtle"
              onClick={() => {
                setTitle(block.title ?? '');
                setBody(block.body ?? '');
                setUrl(block.url ?? '');
              }}
            >
              Отменить
            </Button>
          </Group>
        ) : null}
      </Stack>
    </Paper>
  );
}

type BlockBuilderProps = {
  blocks: TrainingBlockView[];
  saving: boolean;
  onAdd: (type: TrainingBlockType) => void;
  onSave: BlockEditorProps['onSave'];
  onDelete: (blockId: string) => void;
  onReorder: (blockIds: string[]) => void;
};

export function BlockBuilder({
  blocks,
  saving,
  onAdd,
  onSave,
  onDelete,
  onReorder,
}: BlockBuilderProps) {
  const [newType, setNewType] = useState<TrainingBlockType>('text');

  const move = (blockId: string, direction: -1 | 1) => {
    const index = blocks.findIndex((block) => block.id === blockId);
    const target = index + direction;
    if (index === -1 || target < 0 || target >= blocks.length) return;

    const next = [...blocks];
    const [moved] = next.splice(index, 1);
    next.splice(target, 0, moved!);
    onReorder(next.map((block) => block.id));
  };

  return (
    <Stack gap="md">
      {blocks.length === 0 ? (
        <Text size="sm" c="var(--text-muted)">
          В материале пока нет блоков. Добавьте первый — материал нельзя опубликовать пустым.
        </Text>
      ) : (
        blocks.map((block, index) => (
          <BlockEditor
            key={block.id}
            block={block}
            index={index}
            total={blocks.length}
            saving={saving}
            onSave={onSave}
            onDelete={onDelete}
            onMove={move}
          />
        ))
      )}

      <Paper
        withBorder
        p="md"
        radius="lg"
        style={{ borderStyle: 'dashed', borderColor: 'var(--border-subtle)' }}
      >
        <Group gap="sm" align="flex-end">
          <Select
            label="Тип блока"
            w={220}
            data={TRAINING_BLOCK_TYPES.map((type) => ({
              value: type,
              label: TRAINING_BLOCK_LABELS[type],
            }))}
            value={newType}
            onChange={(value) => value && setNewType(value as TrainingBlockType)}
          />
          <Button leftSection={<IconPlus size={16} />} onClick={() => onAdd(newType)} loading={saving}>
            Добавить блок
          </Button>
        </Group>
      </Paper>
    </Stack>
  );
}
